/**
 * TestsPanel — the process-tests surface beside ProcessMode (P7).
 *
 * Lists the active process's `test` declarations with a PASS/FAIL/OPEN/ERROR
 * chip and a coverage badge. Clicking a non-passing test asks the shell to load
 * its answered trace into the scrubber (pins applied) — the wiring lives in the
 * `onRunTest` callback the composition supplies (see `applyTestPins`). Pure view:
 * no playback access, no engine calls — state comes in via props.
 */
import type { ReactElement } from "react";
import { useContextMenu, type ContextMenuItem, type ContextMenuEvent } from "@archlang/ui";
import { useTranslation } from "../i18n";
import type { ProcessTestEntry, ProcessTests } from "./useProcessTests.js";
import type { TestDecl } from "@archlang/engine";

export interface TestsPanelProps {
  readonly tests: ProcessTests;
  /** Load a test's answered trace into the scrubber (pins applied). */
  readonly onRunTest: (decl: TestDecl) => void;
}

const CHIP: Record<string, string> = {
  passed: "bg-emerald-500/15 text-emerald-300",
  failed: "bg-rose-500/15 text-rose-300",
  open: "bg-amber-500/15 text-amber-300",
  errored: "bg-fuchsia-500/15 text-fuchsia-300",
};

const LABEL: Record<string, string> = {
  passed: "PASS",
  failed: "FAIL",
  open: "OPEN",
  errored: "ERROR",
};

export function TestsPanel({ tests, onRunTest }: TestsPanelProps): ReactElement {
  const { entries, coverage } = tests;
  const { t } = useTranslation("nav");
  const menu = useContextMenu();

  // Hoisted out of the row map (the hook must not run in a callback): builds
  // the per-test menu from handlers/data already in scope.
  const itemsFor = (entry: ProcessTestEntry): ContextMenuItem[] => {
    const { decl, result } = entry;
    return [
      ...(result.status !== "passed"
        ? [{ label: t("menu.open"), onClick: () => onRunTest(decl) }]
        : []),
      {
        label: t("menu.copyName"),
        separator: result.status !== "passed",
        onClick: () => void navigator.clipboard?.writeText(decl.name),
      },
    ];
  };

  return (
    <div className="flex flex-col gap-2 p-3 text-[13px] text-slate-200" data-testid="tests-panel">
      <div className="flex items-center justify-between">
        <span className="font-medium text-slate-300">Tests</span>
        {coverage ? (
          <span
            className="rounded-md bg-white/[0.06] px-2 py-0.5 text-[11px] text-slate-300"
            data-testid="coverage-badge"
          >
            {coverage.covered}/{coverage.total} steps ({coverage.pct}%)
          </span>
        ) : null}
      </div>
      {entries.length === 0 ? (
        <p className="text-slate-500">No tests for this process.</p>
      ) : (
        <ul className="flex flex-col gap-1">
          {entries.map((e) => (
            <TestRow
              key={testKey(e)}
              entry={e}
              onRunTest={onRunTest}
              onContextMenu={(ev) => menu.open(ev, itemsFor(e))}
            />
          ))}
        </ul>
      )}
      {menu.node}
    </div>
  );
}

function TestRow({
  entry,
  onRunTest,
  onContextMenu,
}: {
  readonly entry: ProcessTestEntry;
  readonly onRunTest: (decl: TestDecl) => void;
  readonly onContextMenu: (e: ContextMenuEvent) => void;
}): ReactElement {
  const { decl, result } = entry;
  const clickable = result.status !== "passed";
  return (
    <li onContextMenu={onContextMenu}>
      <button
        type="button"
        disabled={!clickable}
        onClick={clickable ? () => onRunTest(decl) : undefined}
        className={`flex w-full items-center gap-2 rounded-md px-2 py-1 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 ${
          clickable ? "hover:bg-white/[0.06]" : "cursor-default"
        }`}
        data-testid="test-row"
        data-status={result.status}
      >
        <span
          className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold ${CHIP[result.status] ?? "bg-white/10 text-slate-300"}`}
        >
          {LABEL[result.status] ?? result.status.toUpperCase()}
        </span>
        <span className="truncate">{decl.name}</span>
      </button>
    </li>
  );
}

function testKey(e: ProcessTestEntry): string {
  return e.result.id ?? `${(e.decl.ownerPath ?? []).join(".")}.${e.decl.name}`;
}
